import { Box, Button, Link, Modal, Typography } from '@mui/material';
import { ApplicationStatus } from '@prisma/client';

import EmailIcon from 'src/assets/images/icons/EmailSVG';
import ScreenIcon from 'src/assets/images/icons/ScreenSVG';
import { Flex } from 'src/components/Flex';
import { DEFAULT_BORDER } from 'src/constants';

interface CaptureSignatureSectionProps {
    onSendEmail: () => void;
    onCaptureOnScreen: () => void;
    showModal: boolean;
    signingUrl: string;
    clearSigningUrl: () => void;
    isCapturingOnScreen: boolean;
    isCreatingAgreement: boolean;
    merchantApplicationStatus: string;
}

export const CaptureSignatureSection: React.FC<CaptureSignatureSection> = ({
    onSendEmail,
    onCaptureOnScreen,
    showModal,
    signingUrl,
    clearSigningUrl,
    isCapturingOnScreen,
    isCreatingAgreement,
    merchantApplicationStatus,
}: CaptureSignatureSectionProps) => {
    // Unique identifier for test IDs, based on merchantApplicationStatus
    const testIdSuffix = merchantApplicationStatus
        ? merchantApplicationStatus.replace(/\s+/g, '')
        : '';
    return (
        <Box
            sx={{
                ...DEFAULT_BORDER,
                bgcolor: 'white',
                boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                padding: '32px 24px',
            }}
        >
            <Typography fontSize="24px" lineHeight="34.3px" fontWeight="500" color="#1D1D1B">
                Signature Capture
            </Typography>
            <Typography
                fontSize="13px"
                fontWeight="400"
                color="#575757"
                lineHeight="18.6px"
                marginTop="8px"
            >
                It&apos;s time to capture the signature of the merchant, please select the best
                method to capture this from the options below.
            </Typography>
            <Box display="flex" alignItems="center" marginTop="24px" gap="16px">
                <Button
                    data-testid={`captureNowOnscreenButton-${testIdSuffix}`}
                    startIcon={<ScreenIcon />}
                    variant="npi-primary"
                    onClick={() => {
                        if (!isCapturingOnScreen) {
                            onCaptureOnScreen();
                        }
                    }}
                    disabled={
                        merchantApplicationStatus === ApplicationStatus.NPI_INTERNAL_CS_REVIEW ||
                        merchantApplicationStatus ===
                            ApplicationStatus.SENT_TO_WORLDPAY_AWAITING_MID ||
                        merchantApplicationStatus ===
                            ApplicationStatus.AWAITING_SUPPORTING_DOCUMENTS
                    }
                >
                    {isCapturingOnScreen
                        ? 'Generating Onscreen Agreement...'
                        : 'Capture Now Onscreen'}
                </Button>

                <Button
                    data-testid={`sendEmailToMerchantButton-${testIdSuffix}`}
                    startIcon={<EmailIcon />}
                    variant="npi-primary"
                    onClick={() => {
                        if (!isCreatingAgreement) {
                            onSendEmail();
                        }
                    }}
                    disabled={
                        merchantApplicationStatus === ApplicationStatus.NPI_INTERNAL_CS_REVIEW ||
                        merchantApplicationStatus ===
                            ApplicationStatus.SENT_TO_WORLDPAY_AWAITING_MID ||
                        merchantApplicationStatus === ApplicationStatus.AWAITING_SIGNATURE ||
                        merchantApplicationStatus ===
                            ApplicationStatus.AWAITING_SUPPORTING_DOCUMENTS
                    }
                >
                    {isCreatingAgreement ? 'Generating Agreement...' : 'Send Email to Merchant'}
                </Button>
            </Box>
            {showModal && (
                <Modal
                    open={showModal}
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                    onClose={() => {
                        return clearSigningUrl();
                    }}
                >
                    <Flex
                        bgcolor="#fff"
                        padding="24px"
                        width="max-content"
                        alignSelf="center"
                        borderRadius="16px"
                    >
                        <Link
                            href={signingUrl}
                            target="_blank"
                            fontSize="30px"
                            fontWeight="600"
                            sx={{ textDecoration: 'none' }}
                        >
                            Sign Document On-Screen
                        </Link>
                    </Flex>
                </Modal>
            )}
        </Box>
    );
};
